import { useCallback, useMemo, useState } from "react";
import { ExtraCategory } from "../../domain/ExtraCategory.enum";
import { CategoryLimits } from "../../domain/entities/CategoryLimits";
import { ValidationIssue } from "../../domain/entities/ValidationIssue";
import { CategoryLimitsContextValue } from "./CategoryLimitsContext";

/**
 * State behind `CategoryLimitsProvider`. The page owns it, because its save handlers live outside
 * the provider and need to store the issues of a rejected save.
 */
export function useCategoryLimitsState(
  limits: CategoryLimits | undefined,
  accommodationName: string
) {
  const [serverIssues, setServerIssues] = useState<readonly ValidationIssue[]>([]);

  const clearServerIssues = useCallback(
    (category: ExtraCategory) =>
      setServerIssues(current =>
        current.some(issue => issue.targetsBlock(category))
          ? current.filter(issue => !issue.targetsBlock(category))
          : current
      ),
    []
  );

  /** Keeps only the issues the section can place in a block; the caller shows the rest. */
  const showServerIssues = useCallback(
    (issues: readonly ValidationIssue[]) =>
      setServerIssues(issues.filter(issue => issue.target.isBlockScoped())),
    []
  );

  const clearAllServerIssues = useCallback(() => setServerIssues([]), []);

  const value = useMemo<CategoryLimitsContextValue>(
    () => ({ limits, accommodationName, serverIssues, clearServerIssues }),
    [limits, accommodationName, serverIssues, clearServerIssues]
  );

  return { value, showServerIssues, clearAllServerIssues };
}
